/**
 * LeftPanel Component - CORE genérico
 *
 * @version 2.2
 * @updated 2026-02-23
 * @module core/layouts/panels
 *
 * @description
 * Panel lateral izquierdo permanente que acepta contenido inyectable de módulos.
 * Por defecto es VISIBLE, y puede ser colapsado manualmente por el usuario.
 *
 * @remarks
 * El botón toggle vive en este componente (LeftPanel) para garantizar que
 * siempre sea visible independientemente del panel hijo renderizado.
 *
 * @example
 * ```tsx
 * <LeftPanel>
 *   <TabbedLeftPanel />
 * </LeftPanel>
 * ```
 */

import { useState, useRef, createContext, useContext } from 'react';
import './left-panel.css';

/* ===== CONTEXT ===== */

interface LeftPanelContextValue {
  collapsed: boolean;
  onToggle: () => void;
}

export const LeftPanelContext = createContext<LeftPanelContextValue>({
  collapsed: false,
  onToggle: () => {},
});

/**
 * Hook para consumir el contexto del panel izquierdo.
 * @returns collapsed y onToggle
 */
export function useLeftPanel(): LeftPanelContextValue {
  return useContext(LeftPanelContext);
}

/* ===== PROPS ===== */

export interface LeftPanelProps {
  /**
   * Callback cuando el panel cambia de estado (visible/collapsed)
   * @param collapsed - true si el panel está colapsado
   */
  onToggle?: (collapsed: boolean) => void;

  /**
   * Estado inicial del panel (por defecto: visible = false collapsed)
   * @default false
   */
  initialCollapsed?: boolean;

  /**
   * Contenido a renderizar dentro del panel (inyectado por módulo)
   */
  children?: React.ReactNode;
}

/* ===== COMPONENT ===== */

/**
 * Botón toggle para colapsar/expandir el panel.
 * Debe renderizarse dentro del content-card para que left:100%
 * siga el borde derecho del card sin animación independiente.
 */
export function LeftPanelToggle(): JSX.Element {
  const { collapsed, onToggle } = useLeftPanel();
  return (
    <button
      type="button"
      className="left-panel-toggle-floating"
      onClick={onToggle}
      aria-label={collapsed ? 'Expandir panel lateral' : 'Colapsar panel lateral'}
      aria-expanded={!collapsed}
    >
      <svg
        viewBox="0 0 88 88"
        fill="currentColor"
        className="floating-icon"
        aria-hidden="true"
        style={{ transform: collapsed ? 'rotate(180deg)' : 'none' }}
      >
        <polygon points="0,5 34,40 0,75 22,75 56,40 22,5" />
        <polygon points="32,5 66,40 32,75 54,75 88,40 54,5" />
      </svg>
    </button>
  );
}

/**
 * Componente LeftPanel
 *
 * @remarks
 * Provee LeftPanelContext a sus hijos. Los paneles hijos renderizan
 * LeftPanelToggle dentro del content-card para que el botón siga
 * perfectamente el borde del card durante la animación.
 */
export function LeftPanel({
  onToggle,
  initialCollapsed = false,
  children,
}: LeftPanelProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [animating, setAnimating] = useState(false);
  const animTimerRef = useRef<ReturnType<typeof setTimeout>>();

  /**
   * Alterna el estado colapsado/visible del panel.
   * Agrega clase 'animating' durante la transición para suspender
   * backdrop-filter y evitar micro-stutters.
   */
  const handleToggle = (): void => {
    setCollapsed(!collapsed);
    onToggle?.(!collapsed);

    // Suspender backdrop-filter durante la animación (280ms = duración de la transición)
    clearTimeout(animTimerRef.current);
    setAnimating(true);
    animTimerRef.current = setTimeout(() => setAnimating(false), 300);
  };

  const panelClasses = [
    'left-panel',
    collapsed ? 'collapsed' : '',
    animating ? 'animating' : '',
  ].filter(Boolean).join(' ');

  return (
    <LeftPanelContext.Provider value={{ collapsed, onToggle: handleToggle }}>
      <aside
        className={panelClasses}
        role="complementary"
        aria-label="Panel lateral izquierdo"
        aria-hidden={collapsed}
        data-collapsed={collapsed}
      >
        <div className="left-panel-content">
          {children}
        </div>
      </aside>
    </LeftPanelContext.Provider>
  );
}
